import cx from 'classnames'
import { ComponentProps, createContext, CSSProperties, forwardRef, useContext, useMemo } from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import gfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { omit } from 'lodash'
import AppTypography from '../AppTypography'
import { useAnchorLink } from './useAnchorLink'
import './styles.scss'

const IS_EXTERNAL_LINK_PREFIX = 'http'

// Components excluded from receiving the global textColor
const COLOR_EXCLUDED_ELEMENTS = ['link', 'code', 'blockquote', 'image']

// Interface for component-specific styles
interface ComponentStylesType {
  paragraph?: CSSProperties
  heading1?: CSSProperties
  heading2?: CSSProperties
  heading3?: CSSProperties
  heading4?: CSSProperties
  heading5?: CSSProperties
  link?: CSSProperties
  list?: CSSProperties
  listItem?: CSSProperties
  code?: CSSProperties
  blockquote?: CSSProperties
  image?: CSSProperties
  // Additional property for any unhandled component type
  [key: string]: CSSProperties | undefined
}

interface MarkdownWrapperProps extends ComponentProps<typeof ReactMarkdown> {
  source: string
  className?: string
  style?: CSSProperties
  /** Global text color to apply to text elements, excluding code blocks, blockquotes, inline code, and links */
  textColor?: string
  /** Specific styles to apply to different component types */
  componentStyles?: ComponentStylesType
}

// Create a context to share color and styles across components
interface MarkdownContextType {
  textColor?: string
  componentStyles?: ComponentStylesType
}

const MarkdownContext = createContext<MarkdownContextType>({})

// Custom hook to access the markdown context
const useMarkdownContext = () => useContext(MarkdownContext)

// Helper to apply component-specific styles with textColor
const applyComponentStyles = (
  componentType: string,
  textColor?: string,
  componentStyles?: ComponentStylesType,
  existingStyle?: CSSProperties
): CSSProperties => {
  const styles: CSSProperties = { ...existingStyle }

  // If this is an excluded element, we should never apply the textColor
  // regardless of its parent
  if (COLOR_EXCLUDED_ELEMENTS.includes(componentType)) {
    // Don't apply textColor
  }
  // Otherwise, apply textColor if provided
  else if (textColor) {
    styles.color = textColor
  }

  // Apply component-specific styles if provided
  if (componentStyles && componentStyles[componentType]) {
    Object.assign(styles, componentStyles[componentType])
  }

  return styles
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const LinkRenderer: Components['link'] = ({ href, children, style, ...props }) => {
  const { componentStyles } = useMarkdownContext()
  const isExternal = href?.startsWith(IS_EXTERNAL_LINK_PREFIX)

  const linkStyle: CSSProperties = {
    ...style
    // For links, we don't reset to initial since they should have their own color
  }

  // Apply component-specific styles
  if (componentStyles?.link) {
    Object.assign(linkStyle, componentStyles.link)
  }

  return (
    <AppTypography.ExternalLink
      href={href}
      strong="semi-bold"
      target={isExternal ? '_blank' : undefined}
      style={linkStyle}
      {...omit(props, 'type')}
    >
      {children}
    </AppTypography.ExternalLink>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const ATagRenderer: Components['a'] = ({ href, children, style, ...props }) => {
  const { componentStyles } = useMarkdownContext()
  const isExternal = href?.startsWith(IS_EXTERNAL_LINK_PREFIX)

  // Only apply component-specific styles, but enforce no text color inheritance
  const linkStyle: CSSProperties = {
    ...style
  }

  // Apply only link-specific styling from componentStyles
  if (componentStyles?.link) {
    Object.assign(linkStyle, componentStyles.link)
  }

  return (
    <AppTypography.ExternalLink
      href={href}
      strong="semi-bold"
      target={isExternal ? '_blank' : undefined}
      style={linkStyle}
      {...omit(props, 'type')}
    >
      {children}
    </AppTypography.ExternalLink>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const Title3: Components['h3'] = ({ children, style, ...props }) => {
  const { textColor, componentStyles } = useMarkdownContext()
  const { anchor, anchorLink } = useAnchorLink({ headerText: children })

  const headingStyle = applyComponentStyles('heading3', textColor, componentStyles, style)

  return (
    <AppTypography.Title {...omit(props, 'ref')} level={2} variant="h3" id={anchor} style={headingStyle}>
      <span>
        {anchorLink}
        {children}
      </span>
    </AppTypography.Title>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const Title4: Components['h4'] = ({ children, style, ...props }) => {
  const { textColor, componentStyles } = useMarkdownContext()
  const { anchor, anchorLink } = useAnchorLink({ headerText: children })

  const headingStyle = applyComponentStyles('heading4', textColor, componentStyles, style)

  return (
    <AppTypography.Title {...omit(props, 'ref')} level={3} variant="h4" id={anchor} style={headingStyle}>
      <span>{anchorLink}</span>
      {children}
    </AppTypography.Title>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const Title5: Components['h5'] = ({ children, style, ...props }) => {
  const { textColor, componentStyles } = useMarkdownContext()
  const { anchor, anchorLink } = useAnchorLink({ headerText: children })

  const headingStyle = applyComponentStyles('heading5', textColor, componentStyles, style)

  return (
    <AppTypography.Title {...omit(props, 'ref')} level={4} id={anchor} style={headingStyle}>
      <span>
        {anchorLink}
        {children}
      </span>
    </AppTypography.Title>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const Paragraph: Components['p'] = ({ children, style, ...props }) => {
  const { textColor, componentStyles } = useMarkdownContext()
  const paragraphStyle = applyComponentStyles('paragraph', textColor, componentStyles, style)

  return (
    <AppTypography.Paragraph {...omit(props, 'ref')} style={paragraphStyle}>
      {children}
    </AppTypography.Paragraph>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const ListRenderer: Components['ul'] = ({ children, style, ...props }) => {
  const { textColor, componentStyles } = useMarkdownContext()
  const listStyle = applyComponentStyles('list', textColor, componentStyles, style)

  return (
    <ul {...props} style={listStyle}>
      {children}
    </ul>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const ListItemRenderer: Components['li'] = ({ children, style, ...props }) => {
  const { textColor, componentStyles } = useMarkdownContext()
  const itemStyle = applyComponentStyles('listItem', textColor, componentStyles, style)

  return (
    <li {...props} style={itemStyle}>
      {children}
    </li>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const CodeBlockRenderer: Components['code'] = ({ children, style, ...props }) => {
  const { componentStyles } = useMarkdownContext()

  const codeStyle: CSSProperties = {
    ...style,
    // Set color to 'initial' to reset to the browser's default color
    color: 'initial'
  }

  // Apply component-specific styles except color
  if (componentStyles?.code) {
    const { color: _, ...codeStylesWithoutColor } = componentStyles.code
    Object.assign(codeStyle, codeStylesWithoutColor)
  }

  return (
    <code {...props} style={codeStyle}>
      {children}
    </code>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const OrderedListRenderer: Components['ol'] = ({ children, style, ...props }) => {
  const { textColor, componentStyles } = useMarkdownContext()
  const listStyle = applyComponentStyles('list', textColor, componentStyles, style)

  return (
    <ol {...props} style={listStyle}>
      {children}
    </ol>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const BlockquoteRenderer: Components['blockquote'] = ({ children, style, ...props }) => {
  const { componentStyles } = useMarkdownContext()

  const blockquoteStyle: CSSProperties = {
    ...style,
    // Set color to 'initial' to reset to the browser's default color
    color: 'initial'
  }

  // Apply component-specific styles except color
  if (componentStyles?.blockquote) {
    const { color: _, ...blockquoteStylesWithoutColor } = componentStyles.blockquote
    Object.assign(blockquoteStyle, blockquoteStylesWithoutColor)
  }

  return (
    <blockquote {...props} style={blockquoteStyle}>
      {children}
    </blockquote>
  )
}

// eslint-disable-next-line react/function-component-definition -- Easier for typing
const ImageRenderer: Components['img'] = ({ src, alt, style, ...props }) => {
  const { componentStyles } = useMarkdownContext()

  const imageStyle: CSSProperties = {
    ...style
    // Images don't have text color, so no need to reset
  }

  // Apply component-specific styles
  if (componentStyles?.image) {
    Object.assign(imageStyle, componentStyles.image)
  }

  return <img src={src} alt={alt} style={imageStyle} {...props} />
}

/**
 * Component to use ReactMarkdown with preset styling and LinkRenderer
 * that opens the a tags in a new tab
 * @param source - markdown source to parse
 * @param className - add custom className on wrapper element
 * @param textColor - optional text color to apply to text elements (excluding code blocks, blockquotes, inline code, and links)
 * @param componentStyles - optional specific styles to apply to different component types
 */
export const MarkdownWrapper = forwardRef<HTMLDivElement, MarkdownWrapperProps>(
  ({ source, className, style, components, textColor, componentStyles, ...rest }: MarkdownWrapperProps, ref) => {
    // Create a context value with the textColor and componentStyles
    const contextValue = useMemo<MarkdownContextType>(
      () => ({ textColor, componentStyles }),
      [textColor, componentStyles]
    )

    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- we need to have a tabIndex to be able to focus the element
      <div ref={ref} style={style} data-testid="markdown-wrapper" tabIndex={0}>
        <MarkdownContext.Provider value={contextValue}>
          <ReactMarkdown
            remarkPlugins={[gfm]}
            // Support br tags which is our fix to handle markdown multi-empty lines
            rehypePlugins={[rehypeRaw]}
            className={cx('app-markdown-wrapper', className)}
            components={{
              ...components,
              link: LinkRenderer,
              a: ATagRenderer,
              p: Paragraph,
              ul: ListRenderer,
              ol: OrderedListRenderer,
              li: ListItemRenderer,
              code: CodeBlockRenderer,
              blockquote: BlockquoteRenderer,
              img: ImageRenderer,
              // We decided to have h3 as the first possible titles in markdown since they are always used in sub-sections
              // we don't want multiple h1 in a page.
              h1: Title3,
              h2: Title3,
              h3: Title3,
              h4: Title4,
              h5: Title5
            }}
            {...rest}
          >
            {source}
          </ReactMarkdown>
        </MarkdownContext.Provider>
      </div>
    )
  }
)
MarkdownWrapper.displayName = 'MarkdownWrapper'

export default MarkdownWrapper
